import type { components } from "../../shared/api/schema.gen";
import { notificationText } from "./notificationText";
import { emergencyText } from "../../shared/i18n/emergencyText";
import { minutesLeftFrom } from "../emergency/emergencyModel";

export type Notification = components["schemas"]["Notification"];
export type NotificationCount = components["schemas"]["NotificationCount"];
export type NotificationChannel = components["schemas"]["NotificationChannel"];

/** Event types the server writes (design §0); used only when `reason` is absent (rows written before Telegram stream B). */
const MENTION_EVENT = "comment.mention";
const TRANSITION_EVENT = "work_item.transitioned";
const SLA_AT_RISK_EVENT = "sla.at_risk";
const SLA_BREACHED_EVENT = "sla.breached";

/** The two lists, in tab order (design §2.1). */
export const CHANNELS: readonly NotificationChannel[] = ["direct", "watching"];

/** A tab's visible name ("Direct" / "Watching"). */
export function tabLabel(channel: NotificationChannel): string {
  return notificationText(channel === "direct" ? "tabDirect" : "tabWatching");
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** "Latest" = the last 7 days (design §2.1); also where the row's time switches from relative to a date (§3.1). */
export const LATEST_WINDOW_MS = 7 * DAY_MS;

/**
 * The label part of a stored title: titles are rendered "<KEY>: <label>" by the writers (WorkflowNotices, the deployment
 * planner, the SLA ticker), so the key — already on line 3 — is dropped. A title in any other shape is returned whole.
 */
export function titleLabel(notification: Pick<Notification, "title" | "item">): string {
  const title = notification.title.trim();
  const key = notification.item?.key;
  if (key && title.startsWith(`${key}: `)) return title.slice(key.length + 2).trim();
  const match = /^[A-Z][A-Z0-9_]*-\d+: (.+)$/u.exec(title);
  return match?.[1]?.trim() ?? title;
}

/**
 * Line 1 of a row (design §3.1 item 2, §3.2).
 * - `actor`: "<Actor> <phrase>" — the actor's name is resolved by the caller (live, from the directory; "TFG team" when it
 *   cannot be).
 * - `system`: no actor, a Giraffe system tile; `tone` marks the SLA notices (amber at risk, red breached).
 * - `title`: an unknown reason or event type — the stored title, so line 1 is never blank (AC-P5).
 */
export type RowLine =
  | { kind: "actor"; phrase: string; tag?: string }
  | { kind: "system"; text: string; tone: "risk" | "breach" }
  | { kind: "title"; text: string };

function withDetail(phrase: string, detail: string | null | undefined): string {
  return detail ? `${phrase}: ${detail}` : phrase;
}

function slaLine(notification: Notification, breached: boolean): RowLine {
  const head = notificationText(breached ? "slaBreached" : "slaAtRisk");
  const label = titleLabel(notification);
  // The SLA ticker titles "<KEY>: SLA at risk (resolution)"; keep what follows its own words.
  const rest = label.replace(/^SLA (at risk|breached)\s*/iu, "").trim();
  return { kind: "system", text: rest ? `${head} ${rest}` : head, tone: breached ? "breach" : "risk" };
}

function deleted(notification: Notification, phrase: string): string {
  return notification.commentDeleted ? `${phrase} ${notificationText("commentDeleted")}` : phrase;
}

/**
 * Batch 17 Emergency OTP (design §6.5, §7): the notices about a code request, a grant, a bypassed move and its end, by event
 * type (their `reason` is `emergency`). Titles never carry a name; the actor is the requester or the holder, resolved live.
 * A request's row says how long its code still lives (every code lives exactly 10 minutes from the notice).
 */
function emergencyLine(notification: Notification, now: Date): RowLine | null {
  switch (notification.eventType) {
    case "emergency_otp.requested": {
      const minutes = minutesLeftFrom(notification.createdAt, now.getTime());
      const tail = minutes > 0 ? emergencyText("bellExpiresIn", { n: minutes }) : emergencyText("bellExpired");
      return { kind: "actor", phrase: `${emergencyText("bellRequested")} · ${tail}` };
    }
    case "emergency_otp.locked":
      return { kind: "system", text: emergencyText("bellLocked"), tone: "breach" };
    case "emergency_bypass.granted":
      return { kind: "actor", phrase: emergencyText("bellGranted") };
    case "emergency_bypass.move": {
      // The title is "<KEY>: <from> → <to> under emergency bypass" (design §7); keep the move.
      const move = titleLabel(notification).replace(/\s*under emergency bypass\s*$/iu, "").trim();
      return { kind: "actor", phrase: withDetail(emergencyText("bellMoved"), move) };
    }
    case "emergency_bypass.ended":
      return { kind: "title", text: emergencyText("bellEnded") };
    case "emergency_bypass.still_active":
      return { kind: "system", text: emergencyText("bellStillActive"), tone: "risk" };
    default:
      return null;
  }
}

/** The role a workflow notice was routed by (batch 23, GRF-71), or null. */
type NoticeRole = "dev" | "sa" | "tester";

function noticeRole(notification: Notification): NoticeRole | null {
  const role = notification.role;
  return role === "dev" || role === "sa" || role === "tester" ? role : null;
}

/**
 * "moved this (Start Testing)": the move's label from the title. The payload carries no target status, and the item's live
 * status may have moved on since, so the row names the move that sent the notice (line 3 shows the live status).
 */
function movedPhrase(notification: Notification): string {
  return transitionDetail(notificationText("moved"), notification);
}

/**
 * Batch 23 (GRF-71, UX-B23 §5): a workflow notice routed by the item's Developer, SA or Tester. Reason `role` = the item names me
 * ("…: you are the tester", tag "As tester"); `role` set with any other reason = nobody is named and every holder was told
 * ("…: sent to every tester, none is named"). Null when the notice carries no role.
 */
function roleLine(notification: Notification, reason: string | null, base: () => RowLine): RowLine | null {
  const role = noticeRole(notification);
  if (!role) return null;
  if (reason === "role") {
    return { kind: "actor", phrase: `${movedPhrase(notification)}: ${notificationText(`roleYou_${role}`)}`, tag: notificationText(`roleTag_${role}`) };
  }
  const every = notificationText(`roleEvery_${role}`);
  if (reason === null) return { kind: "actor", phrase: `${movedPhrase(notification)}: ${every}` };
  const line = base();
  return line.kind === "actor" ? { kind: "actor", phrase: `${line.phrase}: ${every}` } : line;
}

export function rowLine(notification: Notification, now: Date = new Date()): RowLine {
  const emergency = emergencyLine(notification, now);
  if (emergency) return emergency;
  if (notification.watchKind) {
    const internal = notification.excerpt?.isInternal === true;
    switch (notification.watchKind) {
      case "comment":
        return { kind: "actor", phrase: deleted(notification, notificationText(internal ? "addedNote" : "commented")) };
      case "status":
        // The title carries the move ("<KEY>: Status In Progress → Testing", design §6.4); the names are not in the payload.
        return { kind: "actor", phrase: withDetail(notificationText("changedStatus"), titleLabel(notification).replace(/^Status\s+/u, "")) };
      case "assignee":
        // A title never names a person (PDPA, SA-04 §9) and the payload carries no assignee: the phrase stays generic.
        return { kind: "actor", phrase: notificationText("changedAssignee") };
      case "multiple":
        return {
          kind: "actor",
          phrase: deleted(notification, notification.commentId !== null && notification.commentId !== undefined ? notificationText("multipleComment") : notificationText("multiple")),
        };
      default:
        return { kind: "title", text: notification.title };
    }
  }
  const reason = notification.reason ?? (notification.eventType === MENTION_EVENT ? "mention" : notification.eventType === SLA_AT_RISK_EVENT ? "sla_at_risk" : notification.eventType === SLA_BREACHED_EVENT ? "sla_breached" : null);
  const isDeployment = notification.eventType.startsWith("deployment.");
  return roleLine(notification, reason, () => reasonLine(notification, reason, isDeployment)) ?? reasonLine(notification, reason, isDeployment);
}

function reasonLine(notification: Notification, reason: string | null, isDeployment: boolean): RowLine {
  switch (reason) {
    case "mention":
      return { kind: "actor", phrase: deleted(notification, notificationText(notification.excerpt?.isInternal ? "mentionNote" : "mentionComment")) };
    case "assigned":
      return { kind: "actor", phrase: notificationText("assigned") };
    case "returned":
      if (isDeployment) return { kind: "actor", phrase: notificationText("returnedDeployment") };
      return { kind: "actor", phrase: transitionDetail(notificationText("returned"), notification) };
    case "approval":
      // The approval batch notice is about no single item: its title ("3 deployments awaiting your approval") says more.
      return notification.item ? { kind: "actor", phrase: notificationText("approval") } : { kind: "title", text: notification.title };
    case "devops":
      return { kind: "actor", phrase: notificationText("devops") };
    case "role":
      // A `role` notice that lost its role (never written so; kept readable).
      return { kind: "actor", phrase: movedPhrase(notification) };
    case "sla_at_risk":
      return slaLine(notification, false);
    case "sla_breached":
      return slaLine(notification, true);
    case "status_report":
      return statusReminderLine(notification);
    case null:
      if (notification.item && (notification.eventType === TRANSITION_EVENT || isDeployment)) {
        return { kind: "actor", phrase: withDetail(notificationText("updated"), titleLabel(notification)) };
      }
      return { kind: "title", text: notification.title };
    default:
      return { kind: "title", text: notification.title };
  }
}

/**
 * Batch 26 R2: the weekly status reminder (Wednesday / Thursday 09:00 to a Capt. whose project has not reported). No item and no
 * actor: a system line naming the project, taken from the link (`/p/{key}/status`); the stored English title when the link has
 * another shape.
 */
function statusReminderLine(notification: Notification): RowLine {
  const match = /^\/p\/([^/]+)\/status(?:[/?#]|$)/u.exec(notification.linkPath);
  if (!match?.[1]) return { kind: "title", text: notification.title };
  return { kind: "system", text: notificationText("statusReminder", { project: decodeURIComponent(match[1]) }), tone: "risk" };
}

/** "returned this to you (Not pass)": the transition label from the title, when there is one. */
function transitionDetail(phrase: string, notification: Notification): string {
  const label = titleLabel(notification);
  return label && label !== notification.title.trim() ? `${phrase} (${label})` : phrase;
}

export function isUnread(notification: Pick<Notification, "readAt">): boolean {
  return notification.readAt === null || notification.readAt === undefined;
}

/** Splits a page of rows into the "Latest" (last 7 days) and "Older" groups, order kept (design §2.1). */
export function groupRows<T extends Pick<Notification, "createdAt">>(rows: readonly T[], now: Date = new Date()): { latest: T[]; older: T[] } {
  const cutoff = now.getTime() - LATEST_WINDOW_MS;
  const latest: T[] = [];
  const older: T[] = [];
  for (const row of rows) {
    const at = Date.parse(row.createdAt);
    (Number.isNaN(at) || at >= cutoff ? latest : older).push(row);
  }
  return { latest, older };
}

/** The spoken age for a row's accessible name ("4 days ago"); the visible text stays the short `formatRelative`. */
export function spokenAge(iso: string, now: Date = new Date()): string {
  const minutes = Math.round((now.getTime() - Date.parse(iso)) / 60000);
  if (Number.isNaN(minutes) || minutes < 1) return notificationText("justNow");
  if (minutes < 60) return notificationText("minutesAgo", { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return notificationText("hoursAgo", { n: hours });
  return notificationText("daysAgo", { n: Math.round(hours / 24) });
}

/** What the merged bell knows about one of its two sources: the value once loaded, and whether its last read failed. */
export interface BellSource<T> {
  data: T | undefined;
  failed: boolean;
}

/** The chip's text: 1–99, then "99+"; null = no chip (batch 17 attention-bell merge §1). */
export function chipText(n: number): string | null {
  if (n <= 0) return null;
  return n > 99 ? "99+" : String(n);
}

/**
 * The merged bell (batch 17 attention-bell merge §1, §4; ruling D1 (a), superseding P1). One rule: the NUMBER is work waiting on
 * you (the Needs-attention total, already filtered by `attentionCount`), the DOT is any unread notification, Direct or Watching.
 * The accessible name carries both, with the real number (the chip says 99+ above 99), and says which source could not be read.
 * A source still loading counts as nothing yet.
 */
export function bellState(attention: BellSource<number>, count: BellSource<NotificationCount>): { chip: string | null; dot: boolean; name: string } {
  const total = attention.failed ? 0 : (attention.data ?? 0);
  const dot = !count.failed && !!count.data && count.data.direct + count.data.watching > 0;
  const parts: string[] = [];
  if (attention.failed && count.failed) parts.push(notificationText("bellBothFailed"));
  else {
    if (attention.failed) parts.push(notificationText("bellAttentionFailed"));
    else if (total === 1) parts.push(notificationText("bellAttentionOne"));
    else if (total > 1) parts.push(notificationText("bellAttention", { n: total }));
    if (count.failed) parts.push(notificationText("bellCountFailed"));
    else if (dot) parts.push(notificationText("bellUnreadNotices"));
  }
  if (parts.length === 0) parts.push(notificationText("bellNothingNew"));
  return { chip: chipText(total), dot, name: [notificationText("bellName"), ...parts].join(", ") };
}
